import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import SkillsCombobox from '@/components/spark-activities/SkillsCombobox';
import { useExpertTipFilterOptions } from '@/hooks/useExpertTipFilterOptions';
import { toSkillOptions } from './skillOptions';

/** Same cap as the API */
const MAX_SKILLS = 50;

interface ExpertTipDomainSkillsFieldsProps {
  /** '' = no domain */
  domainId: string;
  skillIds: number[];
  onDomainChange: (value: string) => void;
  onSkillsChange: (ids: number[]) => void;
  errors?: { domainId?: string; skillIds?: string };
}

/**
 * Primary domain select and skills picker for the tip create/edit pages.
 * Both are optional; skills may come from any domain, so changing the domain keeps them.
 * The tip details API already leaves out soft-deleted skills, so the edit page never loads them.
 */
export default function ExpertTipDomainSkillsFields({
  domainId,
  skillIds,
  onDomainChange,
  onSkillsChange,
  errors,
}: ExpertTipDomainSkillsFieldsProps) {
  const { data, isLoading, error } = useExpertTipFilterOptions();
  const primaryId = domainId ? parseInt(domainId, 10) : null;

  const skillOptions = useMemo(
    () => toSkillOptions(data?.skills ?? [], primaryId),
    [data?.skills, primaryId]
  );

  if (error) {
    return <p className="text-sm text-red-400">Failed to load domains and skills</p>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="domainId">Primary Domain</Label>
        <select
          id="domainId"
          value={domainId}
          onChange={(e) => onDomainChange(e.target.value)}
          disabled={isLoading}
          className="w-full px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <option value="">None</option>
          {data?.domains.map((domain) => (
            <option key={domain.id} value={domain.id}>
              {domain.title}
            </option>
          ))}
        </select>
        {errors?.domainId && <p className="text-sm text-red-400">{errors.domainId}</p>}
      </div>

      <div className="space-y-2">
        <Label>
          Skills
          <span className="ml-1 font-normal text-pine-ink-soft">(any domain, optional)</span>
        </Label>
        <SkillsCombobox
          skills={skillOptions}
          selectedIds={skillIds}
          onChange={onSkillsChange}
          max={MAX_SKILLS}
          error={errors?.skillIds}
          hideLabel
        />
      </div>
    </div>
  );
}
